import React, { useCallback, useEffect, useRef, useState } from "react"
import "../css/TruncatedList.css"

/**
 * @typedef {Object} TruncatedListProps
 * @property {Array<string>} items - Array of items to display
 * @property {string} [placeholder="--"] - Text to show when items array is empty
 * @property {string} [className] - Additional CSS class for the container
 * @property {boolean} [defaultExpanded=false] - Initial expanded state
 * @property {string} [separator=", "] - Separator between items in collapsed state
 */

/**
 * Calculates how many items can fit in the container width.
 * Uses getComputedStyle to derive gap from CSS (Wolf Fix #1).
 * Codex P1 Fix: First checks if all items fit without overflow indicator.
 *
 * @param {HTMLElement} itemsContainer - The items container element (.truncated-list__items)
 * @param {HTMLElement[]} itemElements - Array of item elements
 * @param {HTMLElement[]} overflowElements - Array of overflow indicator elements for each potential count
 * @returns {number} Number of items that fit
 */
function calculateVisibleCount(itemsContainer, itemElements, overflowElements) {
  if (!itemsContainer || itemElements.length === 0) {
    return itemElements.length
  }

  const containerWidth = itemsContainer.getBoundingClientRect().width
  // Wolf Fix #1: derive gap from CSS instead of hard-coded pixels
  // Adjudication Fix #2: read gap from items container (row flex), not root (column flex)
  const computedStyle = getComputedStyle(itemsContainer)
  const gap = parseFloat(computedStyle.gap) || 0

  // Codex P1 Fix: First calculate total width of all items WITHOUT overflow indicator
  // If everything fits, no need to show overflow at all
  let totalWidthAllItems = 0
  for (let i = 0; i < itemElements.length; i++) {
    const itemWidth = itemElements[i].getBoundingClientRect().width
    totalWidthAllItems += itemWidth + (i > 0 ? gap : 0)
  }

  // If all items fit without overflow, return full count
  if (totalWidthAllItems <= containerWidth) {
    return itemElements.length
  }

  // Otherwise, calculate how many items fit WITH overflow indicator
  let totalWidth = 0
  let count = 0

  for (let i = 0; i < itemElements.length; i++) {
    const itemWidth = itemElements[i].getBoundingClientRect().width
    const widthNeeded = itemWidth + (i > 0 ? gap : 0)

    // Check if adding this item (plus overflow indicator) fits
    const remainingItems = itemElements.length - (i + 1)
    // Adjudication Fix #1: use actual overflow width for this specific count
    const overflowWidth =
      overflowElements[remainingItems - 1]?.getBoundingClientRect().width || 80
    const spaceForOverflow = overflowWidth + gap

    if (totalWidth + widthNeeded + spaceForOverflow <= containerWidth) {
      totalWidth += widthNeeded
      count++
    } else {
      break
    }
  }

  return Math.max(1, count) // Always show at least 1 item
}

/**
 * Displays a list of items with truncation and expand/collapse functionality.
 * When collapsed, shows items inline until out of space, then "+N more ...".
 * When expanded, shows all items with "Show less" option.
 *
 * Wolf Fix #2: Uses <button type="button"> with aria-expanded for keyboard accessibility.
 *
 * @param {TruncatedListProps} props - Component props
 * @returns {React.ReactElement} The rendered component
 */
export function TruncatedList({
  items = [],
  placeholder = "--",
  className = "",
  defaultExpanded = false,
  separator = ", ",
  ...rest
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const [calculatedVisibleCount, setCalculatedVisibleCount] = useState(items.length)
  const measureRef = useRef(null)
  const itemsContainerRef = useRef(null)

  const visibleCount = calculatedVisibleCount

  // Recalculate visible count when container size changes
  useEffect(() => {
    if (!itemsContainerRef.current || isExpanded) {
      return
    }

    const measure = () => {
      if (!measureRef.current || !itemsContainerRef.current) return

      const itemElements = measureRef.current.querySelectorAll(
        ".truncated-list__measure-item"
      )
      // Adjudication Fix #1: measure overflow elements for each potential count
      const overflowElements = measureRef.current.querySelectorAll(
        ".truncated-list__measure-overflow"
      )

      const count = calculateVisibleCount(
        itemsContainerRef.current,
        Array.from(itemElements),
        Array.from(overflowElements)
      )
      setCalculatedVisibleCount(count)
    }

    // Initial measurement
    measure()

    // Set up ResizeObserver for container width changes
    // Adjudication Fix #2: observe items container for accurate width
    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(itemsContainerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [items, isExpanded, separator])

  const handleToggle = useCallback(() => {
    setIsExpanded(prev => !prev)
  }, [])

  const hasItems = items.length > 0

  if (!hasItems) {
    return (
      <div className={`truncated-list ${className}`.trim()} {...rest}>
        <span>{placeholder}</span>
      </div>
    )
  }

  // Calculate visible items and overflow count
  const visibleItems = isExpanded ? items : items.slice(0, visibleCount)
  const overflowCount = items.length - visibleCount
  const hasOverflow = !isExpanded && overflowCount > 0

  return (
    <div
      className={`truncated-list ${isExpanded ? "truncated-list--expanded" : ""} ${className}`.trim()}
      {...rest}
    >
      {/* Hidden measurement container for calculating widths */}
      {!isExpanded && (
        <div
          ref={measureRef}
          className="truncated-list__measure"
          aria-hidden="true"
        >
          {/* Wolf Fix #5: text-node separators included in measurement */}
          {items.map((item, index) => (
            <span key={index} className="truncated-list__measure-item">
              {item}
              {index < items.length - 1 ? separator : ""}
            </span>
          ))}
          {/* Adjudication Fix #1: measure overflow for each potential count (1 to items.length-1) */}
          {items.slice(1).map((_, index) => (
            <span key={`overflow-${index}`} className="truncated-list__measure-overflow">
              +{index + 1} more ...
            </span>
          ))}
        </div>
      )}

      <div ref={itemsContainerRef} className="truncated-list__items">
        {/* Wolf Fix #5: text-node separators in render (no CSS ::after) */}
        {/* ENG-2074 Fix: Use visibleItems.length so separators render in both collapsed and expanded states */}
        {visibleItems.map((item, index) => (
          <span key={index} className="truncated-list__item">
            {item}
            {index < visibleItems.length - 1 ? separator : ""}
          </span>
        ))}
        {/* Wolf Fix #2: Use <button type="button"> for keyboard accessibility */}
        {hasOverflow && (
          <button
            type="button"
            className="truncated-list__toggle"
            onClick={handleToggle}
            aria-expanded={isExpanded}
            aria-label={`Show ${overflowCount} more items`}
          >
            +{overflowCount} more ...
          </button>
        )}
      </div>
      {/* Wolf Fix #2: Use <button type="button"> for "Show less" too */}
      {isExpanded && (
        <button
          type="button"
          className="truncated-list__toggle truncated-list__show-less"
          onClick={handleToggle}
          aria-expanded={isExpanded}
        >
          Show less
        </button>
      )}
    </div>
  )
}
